<!--
  TripleNetworkLogo.vue — animated brand mark for the v2 header.
  Pure SVG with SMIL animations. ~3KB inline. Multi-tenant via :brand prop.
-->
<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :viewBox="viewBox"
    role="img"
    :aria-label="ariaLabel"
    class="tn-logo"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <linearGradient id="tn-blueFace" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#4aa0e6"/>
        <stop offset="0.5" stop-color="#1f6fc4"/>
        <stop offset="1" stop-color="#103f7e"/>
      </linearGradient>
      <linearGradient id="tn-grayFace" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#f2f4f6"/>
        <stop offset="0.5" stop-color="#b9bfc6"/>
        <stop offset="1" stop-color="#7c828b"/>
      </linearGradient>
      <linearGradient id="tn-swoosh" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="#1f6fc4"/>
        <stop offset="1" stop-color="#5fb0ee"/>
      </linearGradient>
      <radialGradient id="tn-nodeGlow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#ffffff"/>
        <stop offset="0.4" stop-color="#5ab0ff"/>
        <stop offset="1" stop-color="#5ab0ff" stop-opacity="0"/>
      </radialGradient>
      <path id="tn-swooshPath" d="M 70,165 C 210,55 470,55 600,120"/>
    </defs>

    <polygon points="120,40 40,160 165,150" fill="rgba(31,111,196,0.12)"
             stroke="#1f6fc4" stroke-width="6" stroke-linejoin="round"/>
    <g fill="#1f6fc4">
      <circle cx="120" cy="40" r="10"/>
      <circle cx="165" cy="150" r="10"/>
      <circle cx="40" cy="160" r="16"/>
      <circle cx="40" cy="160" r="7" fill="#bfe0ff"/>
      <circle cx="92" cy="100" r="6" fill="#5fb0ee"/>
    </g>

    <use href="#tn-swooshPath" fill="none" stroke="url(#tn-swoosh)" stroke-width="13" stroke-linecap="round"/>
    <use href="#tn-swooshPath" fill="none" stroke="#bfe0ff" stroke-width="2.5" stroke-opacity="0.6"/>
    <polygon points="596,98 632,120 596,142" fill="#2f8fe0"/>

    <g>
      <circle r="9" fill="url(#tn-nodeGlow)">
        <animate attributeName="r" values="7;12;7" dur="2.6s" repeatCount="indefinite"/>
      </circle>
      <circle r="3" fill="#ffffff"/>
      <animateMotion dur="5.5s" repeatCount="indefinite" rotate="auto">
        <mpath href="#tn-swooshPath"/>
      </animateMotion>
    </g>

    <g v-if="variant === 'mark'" font-family="'Arial Black', Arial, sans-serif" font-weight="900" font-size="250">
      <text x="196" y="212" fill="#0b2c52">T</text>
      <text x="190" y="207" fill="url(#tn-blueFace)" stroke="#0b2c52" stroke-width="1.5">T</text>
      <text x="356" y="212" fill="#2b3036">N</text>
      <text x="350" y="207" fill="url(#tn-grayFace)" stroke="#4a5058" stroke-width="1.5">N</text>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  variant?: 'mark' | 'wordless';
  brand?: string;
}>(), {
  variant: 'mark',
  brand: 'triple-network',
});

const ariaLabel = computed(() => {
  const labels: Record<string, string> = { 'triple-network': 'Triple-Network' };
  return labels[props.brand] ?? props.brand;
});

const viewBox = computed(() =>
  props.variant === 'mark' ? '20 30 620 220' : '20 30 620 140'
);
</script>

<style scoped>
.tn-logo {
  display: block;
  height: 100%;
  width: auto;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
}
</style>
